import React, { createContext, useContext, useState } from 'react';
import { isNil, noop } from 'lodash/fp';
import { SortOrder } from '../../../generated/graphql';


type Order = false | SortOrder;

interface SetOrderingContext {
    revenue?: Order;
    quantity?: Order;
    markup?: Order;
    foodCost?: Order;
}

interface ContextProps {
    revenue: false | SortOrder;
    quantity: false | SortOrder;
    markup: false | SortOrder;
    foodCost: false | SortOrder;
    setOrderingContext(args: SetOrderingContext): void;
}

const defaultState: ContextProps = {
    revenue: SortOrder.Desc,
    quantity: false,
    markup: false,
    foodCost: false,
    setOrderingContext: noop,
};

const OrderingContext = createContext<ContextProps>(defaultState);

export const OrderingContextProvider: React.FC = ({ children }) => {
    const context = useProvideOrderingContext();

    return <OrderingContext.Provider value={context}>{children}</OrderingContext.Provider>;
};

export const useOrderingContext = () => {
    return useContext(OrderingContext);
};

function useProvideOrderingContext() {
    const [orderContext, setOrderContext] = useState<Omit<ContextProps, 'setOrderingContext'>>({
        revenue: defaultState.revenue,
        quantity: defaultState.quantity,
        foodCost: defaultState.foodCost,
        markup: defaultState.markup,
    });

    const setOrderingContext = ({ revenue, quantity, foodCost, markup }: SetOrderingContext) => {
        const finalRevenue = isNil(revenue) ? false : revenue;
        const finalQuantity = isNil(quantity) ? false : quantity;
        const finalFoodCost = isNil(foodCost) ? false : foodCost;
        const finalMarkup = isNil(markup) ? false : markup;
        setOrderContext({
            ...orderContext,
            revenue: finalRevenue,
            quantity: finalQuantity,
            foodCost: finalFoodCost,
            markup: finalMarkup,
        });
    };

    return {
        ...orderContext,
        setOrderingContext,
    };
}
